import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Tabs,
  Button,
  Space,
  Alert,
  Drawer,
  message,
  Progress,
  Table,
  Tag,
  Input,
  Divider,
  Spin,
  Empty,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  BarChartOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  SearchOutlined,
  FunnelPlotOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, AdminQuest, CreateQuestPayload, QuestAnalytics } from '../lib/api';
import QuestForm from '../components/QuestForm';
import QuestList from '../components/QuestList';

const { Title, Text } = Typography;
const { Search } = Input;

const Quests: React.FC = () => {
  const [activeTab, setActiveTab] = useState('quests');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingQuest, setEditingQuest] = useState<AdminQuest | undefined>(undefined);
  const [searchText, setSearchText] = useState('');
  const [userIdSearch, setUserIdSearch] = useState('');
  const [searchedUserId, setSearchedUserId] = useState('');
  const queryClient = useQueryClient();

  // Fetch quests
  const {
    data: questsData,
    isLoading: loadingQuests,
    error: questsError,
    refetch: refetchQuests,
  } = useQuery({
    queryKey: ['admin-quests'],
    queryFn: () => adminApi.getQuests(),
    staleTime: 2 * 60 * 1000,
  });

  // Fetch quest stats
  const {
    data: stats,
    isLoading: loadingStats,
    error: statsError,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['admin-quest-stats'],
    queryFn: () => adminApi.getQuestStats(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch analytics
  const {
    data: analytics,
    isLoading: loadingAnalytics,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useQuery({
    queryKey: ['admin-quest-analytics'],
    queryFn: () => adminApi.getQuestAnalytics(),
    staleTime: 5 * 60 * 1000,
    enabled: activeTab === 'analytics',
  });

  // Fetch user progress
  const {
    data: userProgress,
    isLoading: loadingUserProgress,
    error: userProgressError,
    refetch: refetchUserProgress,
  } = useQuery({
    queryKey: ['admin-user-quest-progress', searchedUserId],
    queryFn: () => adminApi.getUserQuestProgress(searchedUserId),
    staleTime: 60 * 1000,
    enabled: !!searchedUserId,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (payload: CreateQuestPayload) => adminApi.createQuest(payload),
    onSuccess: () => {
      message.success('Quest created successfully!');
      setDrawerOpen(false);
      setEditingQuest(undefined);
      queryClient.invalidateQueries({ queryKey: ['admin-quests'] });
      refetchQuests();
    },
    onError: (error: any) => {
      message.error(`Failed to create quest: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ questId, payload }: { questId: string; payload: Partial<CreateQuestPayload> }) =>
      adminApi.updateQuest(questId, payload),
    onSuccess: () => {
      message.success('Quest updated successfully!');
      setDrawerOpen(false);
      setEditingQuest(undefined);
      queryClient.invalidateQueries({ queryKey: ['admin-quests'] });
      refetchQuests();
    },
    onError: (error: any) => {
      message.error(`Failed to update quest: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleFormSubmit = async (values: CreateQuestPayload) => {
    if (editingQuest) {
      await updateMutation.mutateAsync({ questId: editingQuest.quest_id, payload: values });
    } else {
      await createMutation.mutateAsync(values);
    }
  };

  const handleEdit = (quest: AdminQuest) => {
    setEditingQuest(quest);
    setDrawerOpen(true);
  };

  const handleOpenCreate = () => {
    setEditingQuest(undefined);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingQuest(undefined);
  };

  const handleUserSearch = () => {
    if (userIdSearch.trim()) {
      setSearchedUserId(userIdSearch.trim());
    }
  };

  const allQuests = questsData?.quests || [];

  const filteredQuests = allQuests.filter((q) => {
    if (!searchText) return true;
    const lower = searchText.toLowerCase();
    // action_type may be null for compound quests
    const actionTypeMatch = q.action_type ? q.action_type.toLowerCase().includes(lower) : false;
    const compoundMatch = (q.action_requirements || []).some((r) =>
      r.action_type.toLowerCase().includes(lower)
    );
    return (
      q.quest_id.toLowerCase().includes(lower) ||
      q.title.toLowerCase().includes(lower) ||
      q.description.toLowerCase().includes(lower) ||
      actionTypeMatch ||
      compoundMatch
    );
  });

  // ---- Stats summary cards ----
  const renderStats = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={12} sm={6}>
        <Card>
          <Statistic
            title="Total Quests"
            value={stats?.total_quests ?? allQuests.filter((q) => !q.deleted_at).length}
            prefix={<TrophyOutlined />}
            loading={loadingStats}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card>
          <Statistic
            title="Active Quests"
            value={stats?.active_quests ?? allQuests.filter((q) => q.is_active && !q.deleted_at).length}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#3f8600' }}
            loading={loadingStats}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card>
          <Statistic
            title="Onboarding Quests"
            value={stats?.onboarding_quests ?? allQuests.filter((q) => q.is_onboarding).length}
            prefix={<ClockCircleOutlined />}
            valueStyle={{ color: '#1890ff' }}
            loading={loadingStats}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card>
          <Statistic
            title="Users Completed All"
            value={stats?.users_completed_all ?? '—'}
            prefix={<UserOutlined />}
            valueStyle={{ color: '#722ed1' }}
            loading={loadingStats}
          />
        </Card>
      </Col>
    </Row>
  );

  // ---- Analytics tab ----
  const renderAnalytics = () => {
    if (loadingAnalytics) return <Spin size="large" style={{ display: 'block', margin: '40px auto' }} />;
    if (analyticsError) return <Alert type="error" message="Failed to load analytics" />;
    if (!analytics) return <Empty description="No analytics data yet" />;

    const completionColumns = [
      { title: 'Quest', dataIndex: 'title', key: 'title', render: (t: string, r: any) => <Space direction="vertical" size={0}><Text strong>{t}</Text><Text type="secondary" style={{ fontSize: 11 }}>{r.quest_id}</Text></Space> },
      { title: 'Started', dataIndex: 'total_started', key: 'total_started', width: 80 },
      { title: 'Completed', dataIndex: 'total_completed', key: 'total_completed', width: 100, render: (v: number) => <Text style={{ color: '#3f8600' }}>{v}</Text> },
      {
        title: 'Completion Rate',
        dataIndex: 'completion_rate',
        key: 'completion_rate',
        width: 160,
        render: (v: number) => (
          <Space>
            <Progress percent={Math.round(v * 100)} size="small" style={{ width: 80 }} />
            <Text>{Math.round(v * 100)}%</Text>
          </Space>
        ),
        sorter: (a: any, b: any) => a.completion_rate - b.completion_rate,
      },
      {
        title: 'Avg. Time',
        dataIndex: 'avg_completion_time_hours',
        key: 'avg_completion_time_hours',
        width: 100,
        render: (v?: number) => v ? `${v.toFixed(1)}h` : '—',
      },
    ];

    const funnelColumns = [
      { title: 'Step', dataIndex: 'order', key: 'order', width: 60, render: (v: number) => <Tag color="blue">#{v}</Tag> },
      { title: 'Quest', dataIndex: 'title', key: 'title', render: (t: string, r: any) => <Space direction="vertical" size={0}><Text strong>{t}</Text><Text type="secondary" style={{ fontSize: 11 }}>{r.quest_id}</Text></Space> },
      { title: 'Users Reached', dataIndex: 'users_reached', key: 'users_reached', width: 120 },
      { title: 'Completed', dataIndex: 'users_completed', key: 'users_completed', width: 100, render: (v: number) => <Text style={{ color: '#3f8600' }}>{v}</Text> },
      {
        title: 'Drop-off Rate',
        dataIndex: 'drop_off_rate',
        key: 'drop_off_rate',
        width: 140,
        render: (v: number) => {
          const pct = Math.round(v * 100);
          const color = pct > 60 ? '#ff4d4f' : pct > 30 ? '#faad14' : '#3f8600';
          return <Text style={{ color }}>{pct}%</Text>;
        },
      },
    ];

    return (
      <Space direction="vertical" style={{ width: '100%' }} size={24}>
        {/* Dashboard summary */}
        {analytics.dashboard_summary && (
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic title="Active Users" value={analytics.dashboard_summary.total_active_users} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Completed All Quests"
                  value={analytics.dashboard_summary.total_completed_all}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Overall Completion Rate"
                  value={Math.round((analytics.dashboard_summary.overall_completion_rate || 0) * 100)}
                  suffix="%"
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Avg. Quests per User"
                  value={(analytics.dashboard_summary.avg_quests_per_user || 0).toFixed(1)}
                />
              </Card>
            </Col>
          </Row>
        )}

        <Card title={<Space><BarChartOutlined /> Completion Rates by Quest</Space>}>
          <Table
            dataSource={analytics.completion_rates}
            columns={completionColumns}
            rowKey="quest_id"
            pagination={false}
            size="small"
          />
        </Card>

        {analytics.onboarding_funnel && analytics.onboarding_funnel.length > 0 && (
          <Card title={<Space><FunnelPlotOutlined /> Onboarding Funnel</Space>}>
            <Table
              dataSource={analytics.onboarding_funnel}
              columns={funnelColumns}
              rowKey="quest_id"
              pagination={false}
              size="small"
            />
          </Card>
        )}
      </Space>
    );
  };

  // ---- User progress tab ----
  const renderUserProgress = () => (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      <Card size="small">
        <Space>
          <Input
            placeholder="Enter User ID"
            value={userIdSearch}
            onChange={(e) => setUserIdSearch(e.target.value)}
            onPressEnter={handleUserSearch}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />
          <Button type="primary" onClick={handleUserSearch} loading={loadingUserProgress}>
            View Progress
          </Button>
        </Space>
      </Card>

      {userProgressError && (
        <Alert type="error" message="User not found or failed to load progress." />
      )}

      {userProgress && (
        <>
          <Card size="small">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Total Quests"
                  value={userProgress.summary.total_quests}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Completed"
                  value={userProgress.summary.completed_quests}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Progress"
                  value={Math.round(userProgress.summary.progress_percentage)}
                  suffix="%"
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Onboarding Complete"
                  value={userProgress.summary.is_onboarding_complete ? '✅ Yes' : '❌ No'}
                />
              </Col>
            </Row>
            <Progress
              percent={Math.round(userProgress.summary.progress_percentage)}
              style={{ marginTop: 16 }}
            />
          </Card>

          <Card title={`Quest Progress — User: ${userProgress.user_id}`}>
            <Table
              dataSource={userProgress.quests}
              rowKey={(r: any) => r.quest.quest_id}
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Quest',
                  key: 'quest',
                  render: (r: any) => (
                    <Space direction="vertical" size={0}>
                      <Text strong>{r.quest.title}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>{r.quest.quest_id}</Text>
                    </Space>
                  ),
                },
                {
                  title: 'Status',
                  key: 'status',
                  width: 100,
                  render: (r: any) => {
                    const statusColors: Record<string, string> = {
                      completed: 'success',
                      active: 'processing',
                      locked: 'default',
                    };
                    return <Tag color={statusColors[r.status] || 'default'}>{r.status.toUpperCase()}</Tag>;
                  },
                },
                {
                  title: 'Progress',
                  key: 'progress',
                  width: 160,
                  render: (r: any) => {
                    if (r.quest.quest_type === 'count') {
                      const pct = Math.min(100, Math.round((r.current_progress / r.quest.target_count) * 100));
                      return (
                        <Space>
                          <Progress percent={pct} size="small" style={{ width: 80 }} />
                          <Text type="secondary">{r.current_progress}/{r.quest.target_count}</Text>
                        </Space>
                      );
                    }
                    return r.is_completed ? <Text style={{ color: '#3f8600' }}>✅ Done</Text> : <Text type="secondary">—</Text>;
                  },
                },
                {
                  title: 'Completed At',
                  key: 'completed_at',
                  width: 150,
                  render: (r: any) =>
                    r.progress?.completed_at
                      ? new Date(r.progress.completed_at).toLocaleDateString()
                      : '—',
                },
              ]}
            />
          </Card>
        </>
      )}
    </Space>
  );

  const hasError = questsError || statsError;

  return (
    <div className="page-content" style={{ padding: 24 }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Title level={2} style={{ margin: 0 }}>
            <TrophyOutlined style={{ marginRight: 8 }} />
            Quest Management
          </Title>
          <Text type="secondary">Configure onboarding quests, rewards, and track user progress</Text>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                refetchQuests();
                refetchStats();
                if (activeTab === 'analytics') refetchAnalytics();
              }}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreate}
            >
              New Quest
            </Button>
          </Space>
        </Col>
      </Row>

      {hasError && (
        <Alert
          type="warning"
          message="Some data failed to load"
          description="Quest data may be incomplete. Check backend connection."
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {renderStats()}

      <Card bodyStyle={{ padding: 0 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarStyle={{ paddingLeft: 16, paddingRight: 16 }}
          items={[
            {
              key: 'quests',
              label: (
                <Space>
                  <TrophyOutlined />
                  Quests ({allQuests.length})
                </Space>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  <div style={{ marginBottom: 16 }}>
                    <Input
                      placeholder="Search quests by ID, title, description or action..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      prefix={<SearchOutlined />}
                      allowClear
                      style={{ maxWidth: 400 }}
                    />
                  </div>
                  <QuestList
                    quests={filteredQuests}
                    isLoading={loadingQuests}
                    onEdit={handleEdit}
                    onRefresh={refetchQuests}
                  />
                </div>
              ),
            },
            {
              key: 'analytics',
              label: (
                <Space>
                  <BarChartOutlined />
                  Analytics
                </Space>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {renderAnalytics()}
                </div>
              ),
            },
            {
              key: 'user-progress',
              label: (
                <Space>
                  <UserOutlined />
                  User Progress
                </Space>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {renderUserProgress()}
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Create / Edit Drawer */}
      <Drawer
        title={editingQuest ? `Edit Quest: ${editingQuest.title}` : 'Create New Quest'}
        open={drawerOpen}
        onClose={handleCloseDrawer}
        width={720}
        destroyOnClose
      >
        <QuestForm
          initialValues={editingQuest}
          onSubmit={handleFormSubmit}
          onCancel={handleCloseDrawer}
          isLoading={createMutation.isPending || updateMutation.isPending}
          existingQuests={allQuests}
        />
      </Drawer>
    </div>
  );
};

export default Quests;

